import * as THREE from 'three';
import { BLACK, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Troll de turba (docs/CRIATURAS.md §2): era II's regenerator. A tall, gangly, stooped bog troll grown out of peat,
 * roots and black water: arms so long the knuckles drag, a hanging mane of moss and reeds, a long drooping nose,
 * small eyes glowing ink-green, wet glossy skin with bog water running off it, and the bog's leftovers tangled in
 * the moss (a rusted helm, a length of chain, old bones). Three wound seams glow faintly where the flesh knits,
 * each on its own `mend*` bone so the clips can flare them. Opaque shared materials; the ink-green light is its
 * one self-lit material and the bog water its one translucent one, plus a few glow sprites.
 */

const UP = V(0, 1, 0);
/** The knitting light: the seams, the eyes and the drips that catch it share it. */
export const TROLL_MEND = '#46e8a0';

/**
 * Bones: hips (pelvis, a peat loin-clump) with the legs (thigh, shin) under it; the chest bends at the small of the
 * back and carries the head (jaw hinged under the nose), the `mane` of moss over the shoulders and both arms
 * (upper arm, forearm, `hand` at the wrist with its claws). The wound seams: `mend` across the chest, `mendA` on
 * the left upper arm, `mendL` on the right thigh (scale them to flare the knitting light).
 */
export const troll = (o?: BaseOpts): Model => {
  const c = begin(o, 0.8, 47);
  const { g, R, rng } = c;
  const skin = std('#383824', { roughness: 0.34, metalness: 0.08, flatShading: true, bumpScale: 0.05 });
  const skinD = std('#232318', { roughness: 0.4, metalness: 0.06, flatShading: true, bumpScale: 0.06 });
  const peat = std('#1f1810', { roughness: 0.95, flatShading: true, bumpScale: 0.08 });
  const moss = std('#29351a', { roughness: 0.95, flatShading: true, bumpScale: 0.09, side: THREE.DoubleSide });
  const mossD = std('#192113', { roughness: 0.95, flatShading: true, bumpScale: 0.08, side: THREE.DoubleSide });
  const reed = std('#3d4128', { roughness: 0.85, flatShading: true });
  const root = std('#2c2118', { roughness: 0.9, flatShading: true, bumpScale: 0.06 });
  const bone = std('#9a9c8a', { roughness: 0.7, flatShading: true });
  const nail = std('#141510', { roughness: 0.25, metalness: 0.2 });
  const rust = metal('#3a2b22', { roughness: 0.85, metalness: 0.4, bumpScale: 0.09 });
  const water = std('#0b1613', { roughness: 0.04, metalness: 0.35, transparent: true, opacity: 0.72, depthWrite: false, bumpScale: 0 });
  const mend = emis('#b4ffd8', TROLL_MEND, 1.3);

  const SH = [-1, 1].map((k) => V(k * 0.4, 1.74, 0.3));
  const EL = [-1, 1].map((k) => V(k * 0.6, 1.2, 0.36));
  const WR = [-1, 1].map((k) => V(k * 0.6, 0.6, 0.5));
  const hipsJ = joint(g, V(0, 1.02, -0.06), 'hips');
  const chestJ = joint(hipsJ.abs, V(0, 1.18, -0.04), 'chest');
  const ch = chestJ.abs;
  const headJ = joint(ch, V(0, 1.86, 0.5), 'head');
  const jawJ = joint(headJ.abs, V(0, 1.8, 0.68), 'jaw');
  const maneJ = joint(ch, V(0, 2.02, 0.22), 'mane');
  const armJ = [-1, 1].map((k, i) => joint(ch, SH[i]!, k < 0 ? 'armL' : 'armR'));
  const foreJ = [-1, 1].map((k, i) => joint(armJ[i]!.abs, EL[i]!, k < 0 ? 'foreL' : 'foreR'));
  const handJ = [-1, 1].map((k, i) => joint(foreJ[i]!.abs, WR[i]!, k < 0 ? 'handL' : 'handR'));
  const legJ = [-1, 1].map((k) => joint(hipsJ.abs, V(k * 0.2, 1.0, -0.08), k < 0 ? 'legL' : 'legR'));
  const KNEE = [-1, 1].map((k) => V(k * 0.3, 0.56, 0.16));
  const shinJ = [-1, 1].map((k, i) => joint(legJ[i]!.abs, KNEE[i]!, k < 0 ? 'shinL' : 'shinR'));

  /** A lump: a squashed sphere. */
  const lump = (parent: THREE.Object3D, mat: THREE.Material, r: number, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1, seg = 6): THREE.Mesh => {
    const m = M(new THREE.SphereGeometry(r, seg, Math.max(4, seg - 3)), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    return m;
  };
  /** A thin wandering tube (roots, reeds, seams). */
  const tube = (parent: THREE.Object3D, pts: THREE.Vector3[], r: number, mat: THREE.Material, rad = 3, seg = pts.length * 2): THREE.Mesh =>
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.2), seg, r, rad), mat, parent);
  /**
   * A gnarled limb from a (radius r1) to b (radius r2): an open tube whose rings bulge and wander, so arms and legs
   * read as twisted root and peat rather than turned wood.
   */
  const gnarl = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r1: number, r2: number, mat: THREE.Material, seg = 6, rings = 3): THREE.Mesh => {
    const v = new THREE.Vector3().subVectors(b, a);
    const len = v.length();
    const geo = new THREE.CylinderGeometry(r2, r1, len, seg, rings, true);
    const pa = geo.attributes.position as THREE.BufferAttribute;
    const bulge = Array.from({ length: rings + 1 }, (_, i) => (i === 0 || i === rings ? 1 : 1 + 0.22 * R()));
    const off = Array.from({ length: rings + 1 }, (_, i) => (i === 0 || i === rings ? [0, 0] : [R() * r1 * 0.35, R() * r1 * 0.35]));
    for (let i = 0; i < pa.count; i++) {
      const ring = Math.round((len / 2 - pa.getY(i)) / len * rings);
      const f = (bulge[ring] ?? 1) * (1 + 0.12 * Math.sin(i * 2.1));
      const o = off[ring] ?? [0, 0];
      pa.setXYZ(i, pa.getX(i) * f + o[0]!, pa.getY(i), pa.getZ(i) * f + o[1]!);
    }
    geo.computeVertexNormals();
    const m = M(geo, mat, parent);
    m.position.copy(a).addScaledVector(v, 0.5);
    m.quaternion.setFromUnitVectors(UP, v.normalize());
    return m;
  };
  /** A frame scaled by `k` about `at` (in model coordinates) for parts placed in model coordinates. */
  const scaled = (parent: THREE.Object3D, at: THREE.Vector3, k: number): THREE.Group => {
    const o = G(parent, at.x, at.y, at.z);
    o.scale.setScalar(k);
    return G(o, -at.x, -at.y, -at.z);
  };
  /** A root wound round a limb from a to b: a loose helix of `turns` round the axis. */
  const binding = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r: number, turns: number, ph: number): void => {
    const ax = new THREE.Vector3().subVectors(b, a);
    const u = new THREE.Vector3().crossVectors(ax, V(0.3, 0.1, 1)).normalize();
    const w = new THREE.Vector3().crossVectors(ax, u).normalize();
    const pts: THREE.Vector3[] = [];
    const n = Math.round(turns * 5) + 2;
    for (let i = 0; i < n; i++) {
      const k = i / (n - 1), an = ph + k * turns * Math.PI * 2;
      pts.push(a.clone().addScaledVector(ax, k).addScaledVector(u, Math.cos(an) * r).addScaledVector(w, Math.sin(an) * r));
    }
    tube(parent, pts, 0.016, root, 3, n);
  };
  /**
   * A wound seam on its own bone (pivot at its centre): a jagged lip of torn flesh either side, the glowing split
   * between them and dark root-stitches drawn across it. `dir` runs along the seam, `n` is its outward normal.
   */
  const seam = (bone: THREE.Object3D, at: THREE.Vector3, dir: THREE.Vector3, n: THREE.Vector3, len: number): void => {
    const d = dir.clone().normalize();
    const side = new THREE.Vector3().crossVectors(n, d).normalize();
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i < 6; i++) {
      const k = i / 5 - 0.5;
      pts.push(at.clone().addScaledVector(d, k * len).addScaledVector(side, (i % 2 ? 1 : -1) * 0.018 + R() * 0.01));
    }
    tube(bone, pts, 0.017, mend, 3, 6);
    for (const s of [-1, 1]) tube(bone, pts.map((p) => p.clone().addScaledVector(side, s * 0.03).addScaledVector(n, 0.012)), 0.015, skinD, 3, 5);
    for (let i = 0; i < 2; i++) {
      const p = at.clone().addScaledVector(d, (i - 0.5) * len * 0.4);
      tube(bone, [p.clone().addScaledVector(side, -0.05), p.clone().addScaledVector(n, 0.03), p.clone().addScaledVector(side, 0.05)], 0.009, root, 3, 2);
    }
    glowSprite(bone, TROLL_MEND, len * 2.2, at.clone().addScaledVector(n, 0.04), 0.35);
  };

  // A spreading puddle of bog water where it stands.
  const pg = new THREE.CircleGeometry(0.62, 16);
  const pp = pg.attributes.position as THREE.BufferAttribute;
  for (let i = 1; i < pp.count; i++) {
    const f = 0.75 + 0.25 * Math.sin(i * 2.3) + rng() * 0.2;
    pp.setXY(i, pp.getX(i) * f, pp.getY(i) * f);
  }
  const pool = M(pg, water, g);
  pool.rotation.x = -Math.PI / 2;
  pool.position.set(0, 0.008, 0.06);

  // Legs: long and thin, knees bent forward under the stoop, on long splayed feet with three black-nailed toes.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const th = legJ[i]!.abs, sh = shinJ[i]!.abs;
    const hip = V(k * 0.2, 1.0, -0.08), knee = KNEE[i]!, ank = V(k * 0.27, 0.13, -0.1);
    gnarl(th, hip, knee, 0.14, 0.09, skin);
    lump(th, skin, 0.12, k * 0.24, 0.82, 0.0, 1, 1.5, 1.1, 6);
    lump(sh, skinD, 0.09, knee.x, knee.y, knee.z, 1, 1, 1, 5);
    gnarl(sh, knee, ank, 0.085, 0.06, skin, 5);
    // Knobbled calf, and a heel spur.
    lump(sh, skin, 0.075, k * 0.29, 0.38, 0.0, 1, 1.6, 1, 5);
    const foot = lump(sh, skinD, 0.1, k * 0.28, 0.06, 0.06, 0.9, 0.42, 1.9, 6);
    foot.rotation.y = k * 0.2;
    for (let f = 0; f < 3; f++) {
      const a = k * 0.2 + (f - 1) * 0.35;
      const toe = G(sh, k * 0.28 + Math.sin(a) * 0.12, 0.04, 0.2 + Math.cos(a) * 0.08);
      toe.rotation.y = a;
      gnarl(toe, V(0, 0, 0), V(0, -0.01, 0.09), 0.03, 0.022, skinD, 4, 1);
      const nl = M(new THREE.ConeGeometry(0.02, 0.07, 4), nail, toe);
      nl.position.set(0, -0.015, 0.12);
      nl.rotation.x = Math.PI / 2 + 0.4;
    }
    binding(sh, V(k * 0.28, 0.48, 0.08), V(k * 0.27, 0.18, -0.08), 0.07, 1.4, k);
  }
  // The thigh wound (right).
  const mendLJ = joint(legJ[1]!.abs, V(0.27, 0.8, 0.08), 'mendL');
  seam(mendLJ.abs, V(0.27, 0.8, 0.08), V(0.25, -1, 0.35), V(0.2, 0.1, 1).normalize(), 0.22);

  // Pelvis: a narrow bony pelvis under a hanging clump of peat and moss.
  const hp = hipsJ.abs;
  lump(hp, skinD, 0.22, 0, 1.04, -0.04, 1.25, 0.8, 1.0, 7);
  lump(hp, peat, 0.24, 0, 0.95, 0.02, 1.15, 0.7, 1.05, 6).rotation.y = 0.4;
  const loin = M(new THREE.CylinderGeometry(0.26, 0.34, 0.34, 9, 2, true), mossD, hp);
  loin.position.set(0, 0.82, 0.0);
  {
    const p = loin.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) if (p.getY(i) < -0.1) p.setY(i, p.getY(i) - (i % 2 ? 0.12 : rng() * 0.08));
    loin.geometry.computeVertexNormals();
  }

  // Torso: gaunt ribs pitched far forward, a sagging pot of a belly below them and a high hump of shoulder.
  const ribs = lump(ch, skin, 0.3, 0, 1.52, 0.16, 1.05, 1.25, 0.85, 8);
  ribs.rotation.x = 0.85;
  const belly = lump(ch, skin, 0.22, 0, 1.24, 0.14, 1.1, 0.95, 1.05, 7);
  belly.rotation.x = 0.3;
  const hump = lump(ch, skinD, 0.28, 0, 1.86, 0.1, 1.45, 0.75, 1.05, 7);
  hump.rotation.x = 0.55;
  // Ribs showing through the wet skin on the flanks.
  for (const k of [-1, 1]) {
    for (let r = 0; r < 2; r++) {
      const y = 1.66 - r * 0.16, z = 0.35 - r * 0.05;
      tube(ch, [V(k * 0.08, y + 0.04, z + 0.06), V(k * 0.24, y, z - 0.02), V(k * 0.31, y - 0.06, z - 0.18)], 0.022, skin, 3, 3);
    }
  }
  // Knuckles of spine down the back, and peat clods stuck to it.
  for (let i = 0; i < 4; i++) {
    const sp = M(new THREE.IcosahedronGeometry(0.05 - i * 0.004, 0), skinD, ch);
    sp.position.set(0, 1.95 - i * 0.2, -0.08 - i * 0.08);
  }
  for (let i = 0; i < 2; i++) {
    const cl = M(new THREE.IcosahedronGeometry(0.07 + rng() * 0.04, 0), peat, ch);
    cl.position.set(R() * 0.5, 1.4 + rng() * 0.4, -0.12 - rng() * 0.12);
    cl.scale.set(1, 0.6, 1);
  }
  // The chest wound.
  const mendJ = joint(ch, V(-0.05, 1.5, 0.43), 'mend');
  seam(mendJ.abs, V(-0.05, 1.5, 0.43), V(0.7, -0.75, 0.2), V(0, 0.45, 1).normalize(), 0.36);
  // Roots creep out of the wound and over the ribs.
  tube(ch, [V(0.1, 1.38, 0.4), V(0.2, 1.3, 0.34), V(0.26, 1.18, 0.24), V(0.24, 1.06, 0.18)], 0.014, root, 3, 6);
  tube(ch, [V(-0.18, 1.62, 0.38), V(-0.28, 1.66, 0.3), V(-0.36, 1.62, 0.18)], 0.012, root, 3, 5);

  // Neck: thin, thrust out from under the hump.
  gnarl(ch, V(0, 1.86, 0.26), V(0, 1.84, 0.6), 0.1, 0.085, skin, 6, 2);

  // Head: a long skull hung forward, a heavy brow over small glowing eyes, a long drooping nose and drooping ears.
  const HEAD_K = 1.3, HEAD_AT = V(0, 1.86, 0.52);
  const hd = scaled(headJ.abs, HEAD_AT, HEAD_K);
  const skull = lump(hd, skin, 0.15, 0, 1.9, 0.66, 1.05, 0.9, 1.25, 7);
  skull.rotation.x = 0.25;
  const brow = lump(hd, skinD, 0.12, 0, 1.94, 0.78, 1.5, 0.45, 0.75, 6);
  brow.rotation.x = -0.25;
  for (const k of [-1, 1]) {
    lump(hd, BLACK, 0.03, k * 0.065, 1.89, 0.84, 1.2, 0.75, 0.6, 5);
    M(new THREE.OctahedronGeometry(0.014, 0), mend, hd).position.set(k * 0.065, 1.89, 0.86);
    glowSprite(hd, TROLL_MEND, 0.12, V(k * 0.065, 1.89, 0.88), 0.95);
    // Ears: long, torn and hanging down beside the jaw.
    const ear = G(hd, k * 0.15, 1.92, 0.62);
    ear.rotation.set(0.4, k * 0.3, k * 2.3);
    const ec = M(new THREE.ConeGeometry(0.05, 0.24, 4), skin, ear);
    ec.position.y = 0.11;
    ec.scale.z = 0.35;
  }
  // The nose: a long sagging root of flesh, bulbous at the drooping tip.
  const nose = G(hd, 0, 1.88, 0.86);
  nose.rotation.x = 2.35;
  const nc = M(new THREE.CylinderGeometry(0.022, 0.045, 0.24, 6), skin, nose);
  nc.position.y = 0.12;
  lump(nose, skinD, 0.042, 0, 0.25, -0.01, 1, 1.25, 1, 6);
  // A wart or two.
  lump(hd, skinD, 0.022, 0.08, 1.97, 0.7, 1, 1, 1, 4);
  // Jaw: a wide, slack underbite under the nose, a row of crooked teeth, bog water drooling off it.
  const jw = scaled(jawJ.abs, HEAD_AT, HEAD_K);
  const jb = lump(jw, skinD, 0.13, 0, 1.76, 0.74, 1.15, 0.5, 0.95, 7);
  jb.rotation.x = -0.15;
  for (let i = 0; i < 4; i++) {
    const tt = M(new THREE.ConeGeometry(0.014, 0.05 + (i % 2) * 0.03, 4), bone, jw);
    tt.position.set(-0.075 + i * 0.05, 1.8, 0.84 - Math.abs(i - 1.5) * 0.02);
    tt.rotation.z = R() * 0.5;
  }
  const drool = [new Ribbon(jw, [V(-0.05, 1.74, 0.84), V(-0.055, 1.66, 0.85), V(-0.05, 1.56, 0.84)], 0.009, water, rng, 0.012)];
  const dr = M(new THREE.ConeGeometry(0.01, 0.08, 4), water, jw);
  dr.position.set(0.07, 1.7, 0.81);
  dr.rotation.x = Math.PI;

  // Moss and reeds hang off the skull in long wet locks either side of the face.
  const HL: [number, number, number, number][] = [[-0.14, 1.96, 0.6, 0.42], [0.15, 1.95, 0.58, 0.36], [-0.1, 2.0, 0.5, 0.5], [0.08, 2.01, 0.5, 0.46], [0.0, 2.02, 0.56, 0.3]];
  for (const [x, y, z, h] of HL) {
    const lk = M(new THREE.ConeGeometry(0.055, h, 4, 1, true), moss, hd);
    lk.position.set(x * 1.3, y - h / 2, z);
    lk.rotation.set(Math.PI, R() * 0.6, -x * 0.6);
    lk.scale.z = 0.5;
  }
  for (const x of [-0.12, 0.13]) tube(hd, [V(x, 1.98, 0.62), V(x * 1.4, 1.8, 0.66), V(x * 1.5, 1.6, 0.68)], 0.007, reed, 3, 4);
  // The rusted helm some drowned soldier wore, cracked and sunk skew on the skull, choked in moss.
  const helm = G(hd, 0.03, 2.0, 0.6);
  helm.rotation.set(-0.35, 0.3, 0.32);
  const hg = new THREE.SphereGeometry(0.17, 8, 3, 0, Math.PI * 2, 0, Math.PI / 2.1);
  const hpos = hg.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < hpos.count; i++) {
    // A dent in one side, and the rim eaten ragged.
    const x = hpos.getX(i), y = hpos.getY(i), z = hpos.getZ(i);
    const dent = x > 0.06 && y > 0.06 ? 0.85 : 1;
    hpos.setXYZ(i, x * dent, y * dent - (y < 0.02 ? rng() * 0.025 : 0), z);
  }
  hg.computeVertexNormals();
  M(hg, rust, helm);
  const crest = M(new THREE.BoxGeometry(0.02, 0.05, 0.3), rust, helm);
  crest.position.set(0, 0.155, 0);
  crest.rotation.x = 0.1;
  // A broken nasal bar.
  M(new THREE.BoxGeometry(0.03, 0.12, 0.015), rust, helm).position.set(0, -0.02, 0.17);

  // The mane: a ragged shawl of moss over the hump and shoulders, falling in torn strands, with reeds and roots
  // through it; on its own pivot at the nape so it swings after the body.
  const mn = maneJ.abs;
  // Over the back only (the arc behind the shoulders), so the chest and its wound show.
  const mgeo = new THREE.CylinderGeometry(0.28, 0.36, 0.7, 10, 3, true, Math.PI * 0.45, Math.PI * 1.1);
  {
    const p = mgeo.attributes.position as THREE.BufferAttribute;
    const cuts = new Map<number, number>();
    for (let i = 0; i < p.count; i++) {
      const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
      const a = Math.atan2(x, z);
      const k = (0.35 - y) / 0.7;
      // Clumped into hanging locks; the hem torn into long points.
      const f = 1 + 0.14 * k * Math.sin(a * 7 + 0.5);
      p.setXYZ(i, x * f, y, z * f);
      if (k > 0.99) {
        const key = Math.round(a * 1000);
        let cut = cuts.get(key);
        if (cut === undefined) {
          cut = cuts.size % 2 ? -0.16 - rng() * 0.18 : -rng() * 0.05;
          cuts.set(key, cut);
        }
        p.setY(i, p.getY(i) + cut);
      }
    }
    mgeo.computeVertexNormals();
  }
  const shawl = M(mgeo, moss, mn);
  shawl.position.set(0, 1.74, 0.0);
  shawl.rotation.x = 0.22;
  const cap = lump(mn, mossD, 0.3, 0, 1.98, 0.08, 1.35, 0.5, 1.0, 7);
  cap.rotation.x = 0.5;
  // Long locks of moss and reeds hanging off the back and the shoulders.
  const LOCKS: [number, number, number, number][] = [[-0.42, 1.84, 0.22, 0.55], [0.44, 1.82, 0.2, 0.5], [-0.2, 1.9, -0.18, 0.75], [0.18, 1.9, -0.2, 0.7], [0.0, 1.95, -0.16, 0.85], [-0.36, 1.8, -0.02, 0.6], [0.36, 1.78, -0.04, 0.62]];
  for (const [x, y, z, h] of LOCKS) {
    const lk = M(new THREE.ConeGeometry(0.06, h, 4, 1, true), moss, mn);
    lk.position.set(x, y - h / 2, z);
    lk.rotation.set(Math.PI + (z < 0 ? 0.3 : -0.1), R(), x * 0.4);
    lk.scale.z = 0.45;
  }
  for (let i = 0; i < 4; i++) {
    const a = -1.5 + i * 1.0;
    const x = Math.sin(a) * 0.36, z = 0.1 - Math.cos(a) * 0.32;
    const h = 0.55 + rng() * 0.35;
    tube(mn, [V(x, 1.96, z), V(x * 1.1, 1.96 - h * 0.5, z - 0.06), V(x * 1.15 + R() * 0.08, 1.96 - h, z - 0.04)], 0.008, reed, 3, 4);
  }
  // Hanging from the mane: a length of rusted chain looped over the right shoulder, and an old jawbone on a cord.
  for (let i = 0; i < 5; i++) {
    const lk = M(new THREE.TorusGeometry(0.04, 0.012, 3, 4), rust, mn);
    const k = i / 4;
    lk.position.set(0.12 + k * 0.32, 1.95 - Math.sin(k * Math.PI) * 0.2 - k * 0.18, 0.36 - k * 0.12);
    lk.rotation.set(0.4, i % 2 ? Math.PI / 2 : 0, 0.6);
    lk.scale.y = 1.3;
  }
  const relic = G(mn, -0.3, 1.66, 0.34);
  relic.rotation.set(0.2, 0.5, 0.3);
  const jbg = M(new THREE.TorusGeometry(0.06, 0.016, 3, 5, Math.PI), bone, relic);
  jbg.rotation.x = Math.PI / 2;
  for (let i = 0; i < 3; i++) M(new THREE.ConeGeometry(0.009, 0.03, 3), bone, relic).position.set(-0.04 + i * 0.04, 0.02, 0.05);
  // A small bird skull and a finger bone caught in the moss on the back.
  lump(mn, bone, 0.045, 0.16, 1.82, -0.22, 1, 0.8, 1.3, 5);
  limb(mn, V(-0.12, 1.72, -0.28), V(-0.06, 1.62, -0.3), 0.012, 0.01, bone, 4);

  // Arms: long and stringy, swollen at elbow and knuckle, wound in roots; huge hands with long black claws hang
  // to the ground.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const up = armJ[i]!.abs, fo = foreJ[i]!.abs, hn = handJ[i]!.abs;
    const sh = SH[i]!, el = EL[i]!, wr = WR[i]!;
    lump(up, skin, 0.12, sh.x + k * 0.02, sh.y, sh.z, 1.05, 0.9, 1.1);
    // A clump of moss and peat on the shoulder, reeds sticking out of it.
    const cl = lump(up, k < 0 ? moss : mossD, 0.15, sh.x + k * 0.04, sh.y + 0.08, sh.z - 0.02, 1.15, 0.55, 1.2, 6);
    cl.rotation.set(R() * 0.4, R(), k * 0.3);
    gnarl(up, sh, el, 0.11, 0.075, skin);
    lump(up, skin, 0.09, k * 0.5, 1.48, 0.33, 1, 1.6, 1, 6);
    lump(fo, skinD, 0.08, el.x, el.y, el.z, 1, 1, 1, 5);
    gnarl(fo, el, wr, 0.075, 0.1, skin);
    lump(fo, skin, 0.1, k * 0.61, 0.82, 0.45, 1, 1.8, 1, 6);
    binding(fo, V(k * 0.6, 1.1, 0.38), V(k * 0.6, 0.66, 0.48), 0.1, 1.6, 2 * k);
    // A root trailing off the forearm.
    tube(fo, [V(k * 0.68, 0.9, 0.42), V(k * 0.74, 0.7, 0.4), V(k * 0.72, 0.5, 0.36)], 0.01, root, 3, 4);
    // The hand: a broad palm hanging off the wrist, four long fingers curled under to the knuckles, black claws.
    const palm = lump(hn, skinD, 0.12, k * 0.6, 0.48, 0.52, 0.9, 1.25, 0.65, 6);
    palm.rotation.set(0.2, 0, k * 0.15);
    for (let f = 0; f < 4; f++) {
      const sx = k * (0.6 + (f - 1.5) * 0.055), sz = 0.56 + (1.5 - Math.abs(f - 1.5)) * 0.02;
      const a = V(sx, 0.4, sz), b = V(sx + k * (f - 1.5) * 0.02, 0.2, sz + 0.06), cc = V(sx + k * (f - 1.5) * 0.03, 0.08, sz + 0.14);
      gnarl(hn, a, b, 0.03, 0.025, skin, 4, 1);
      gnarl(hn, b, cc, 0.025, 0.018, skinD, 4, 1);
      const cl = M(new THREE.ConeGeometry(0.018, 0.11, 4), nail, hn);
      cl.position.copy(cc).add(V(0, -0.02, 0.05));
      cl.quaternion.setFromUnitVectors(UP, V(0, -0.35, 1).normalize());
    }
    // The thumb.
    const tb = V(k * 0.53, 0.42, 0.6);
    gnarl(hn, V(k * 0.55, 0.5, 0.56), tb, 0.03, 0.024, skin, 4, 1);
    const tc = M(new THREE.ConeGeometry(0.016, 0.08, 4), nail, hn);
    tc.position.copy(tb).add(V(0, -0.03, 0.03));
    tc.rotation.x = Math.PI - 0.6;
    // Bog water dripping off the claws.
    M(new THREE.ConeGeometry(0.012, 0.09, 4), water, hn).position.set(k * 0.62, 0.0, 0.66);
  }
  // The left arm's wound.
  const mendAJ = joint(armJ[0]!.abs, V(-0.53, 1.46, 0.4), 'mendA');
  seam(mendAJ.abs, V(-0.53, 1.46, 0.4), V(-0.2, -1, 0.15), V(-0.2, 0.1, 1).normalize(), 0.24);

  // Bog water running off the body: beads and drips under the chin, belly and elbows.
  const DRIPS: [THREE.Object3D, number, number, number, number][] = [
    [ch, 0.08, 1.06, 0.3, 0.1], [ch, -0.14, 1.12, 0.28, 0.08], [foreJ[0]!.abs, -0.62, 1.08, 0.36, 0.09], [foreJ[1]!.abs, 0.62, 1.08, 0.36, 0.08],
  ];
  for (const [p, x, y, z, h] of DRIPS) {
    const d = M(new THREE.ConeGeometry(0.014, h, 4), water, p);
    d.position.set(x, y - h / 2, z);
    d.rotation.x = Math.PI;
  }
  // A cold breath of the mending light over the hump and in the chest.
  const heart = glowSprite(ch, TROLL_MEND, 0.9, V(0, 1.45, 0.3), 0.12, true);

  // Only the drool and the light's breathing here: every move is the clips' (anim/creatures/troll.ts).
  const t = (n: number): void => {
    for (const r of drool) r.step();
    heart.material.opacity = 0.09 + ((n * 5) % 9) / 90;
  };
  t(0);
  return done(c, 2.15, t, undefined, bonesOf(c, {
    hips: hipsJ.p, chest: chestJ.p, head: headJ.p, jaw: jawJ.p, mane: maneJ.p,
    armL: armJ[0]!.p, foreL: foreJ[0]!.p, handL: handJ[0]!.p, armR: armJ[1]!.p, foreR: foreJ[1]!.p, handR: handJ[1]!.p,
    legL: legJ[0]!.p, shinL: shinJ[0]!.p, legR: legJ[1]!.p, shinR: shinJ[1]!.p,
    mend: mendJ.p, mendA: mendAJ.p, mendL: mendLJ.p,
  }));
};
